import i18 from 'i18next';
import immer from 'immer';
import { LayoutActions, LayoutActionTypes, MainLayoutArea } from 'layout';
import { Reducer } from 'redux';
import { ActionType } from 'typesafe-actions';

import { MessageType } from '@models';

import { LibraryActions } from './';
import * as Actions from './actions';
import {
  ActionTypes,
  FolderPathItem,
  LibraryState,
  MessageLibraryItem
} from './types';

type LibraryActions = ActionType<typeof Actions>;
type LayoutActions = ActionType<typeof LayoutActions>;

const initialState: LibraryState = {
  items: [],
  folderPath: [],
  fetchingMessage: false,
  fetchingLibrary: false,
  deletingMessages: false,
  savingMessage: false,
};

const getRootFolderBasedOnMessageType = (library: MessageLibraryItem[], messageType: MessageType): FolderPathItem => {
  let libItem: MessageLibraryItem | undefined;
  let friendlyFolderName = '';
  switch (messageType) {
    case MessageType.Overhead:
      libItem = library.find((i) => i.mediaInfo.name === 'HARMONY_MESSAGES_OVERHEAD');
      friendlyFolderName = i18.t('overHead');
      break;
    case MessageType.OnHold:
      libItem = library.find((i) => i.mediaInfo.name === 'HARMONY_MESSAGES_ON_HOLD');
      friendlyFolderName = i18.t('onHold');
      break;
  }
  if (!libItem) {
    throw `No library folder found for message type ${messageType}`;
  } else {
    return {
      id: libItem.mediaInfo.id,
      name: friendlyFolderName,
      level: libItem.level,
      messageType,
    };
  }
};

const popFolder = (folderPath: FolderPathItem[], folderToPop: FolderPathItem | undefined) => {
  if (folderToPop) {
    const index = folderPath._findIndexBy('id', folderToPop.id);
    if (index >= 0) {
      return folderPath.slice(0, index + 1);
    }
  } else {
    if (folderPath.length > 1) {
      folderPath.pop();
    }
  }
  return folderPath;
};

export const libraryReducer: Reducer<LibraryState, LibraryActions | LayoutActions> = (state = initialState, action) => {
  return immer(state, (draftState) => {
    switch (action.type) {
      case ActionTypes.RESET_ROOT_FOLDER: {
        const rootFolder = getRootFolderBasedOnMessageType(draftState.items, action.payload.messageType);
        draftState.folderPath = [rootFolder];
        break;
      }
      case LayoutActionTypes.MENU_SELECTION:
        if (
          action.payload.mainArea === MainLayoutArea.LibraryOnHold ||
          action.payload.mainArea === MainLayoutArea.LibraryOverhead
        ) {
          const rootFolder = getRootFolderBasedOnMessageType(draftState.items, Number(action.payload.mainArea));
          draftState.folderPath = [rootFolder];
        }
        break;
      case ActionTypes.FETCH_LIBRARY_REQUEST:
        draftState.fetchingLibrary = true;
        draftState.items = [];
        break;
      case ActionTypes.FETCH_LIBRARY_SUCCESS:
        draftState.items = action.payload.messages;
        draftState.fetchingLibrary = false;
        const rootFolder = getRootFolderBasedOnMessageType(draftState.items, action.payload.messageType);
        draftState.folderPath = [rootFolder];
        break;
      case ActionTypes.PUSH_FOLDER: {
        draftState.folderPath.push({
          id: action.payload.folder.mediaInfo.id,
          name: action.payload.folder.mediaInfo.name,
          level: action.payload.folder.level,
        });
        break;
      }
      case ActionTypes.POP_FOLDER: {
        draftState.folderPath = popFolder(draftState.folderPath, action.payload.folderPathItem);
        break;
      }
      case ActionTypes.ADD_MESSAGES:
        draftState.items = [
          ...action.payload.medias.map<MessageLibraryItem>((mediaDto) => ({
            level: 0,
            mediaInfo: mediaDto,
            messageType: action.payload.messageType,
          })),
          ...draftState.items,
        ];
        break;
      case ActionTypes.FETCH_MESSAGE_INFO_REQUEST:
        draftState.fetchingMessage = true;
        break;
      case ActionTypes.FETCH_MESSAGE_INFO_SUCCESS:
        const index = draftState.items.findIndex((i) => i.mediaInfo.id === action.payload.message.id);
        draftState.items[index].mediaInfo = action.payload.message;
        break;
      case ActionTypes.DELETE_MESSAGES_REQUEST:
        draftState.deletingMessages = true;
        break;
      case ActionTypes.DELETE_MESSAGES_SUCCESS:
        draftState.deletingMessages = false;
        draftState.items = draftState.items.filter((m) => action.payload.mediaIds.indexOf(m.mediaInfo.id) === -1);
        break;
      case ActionTypes.SAVE_MESSAGE_INFO_REQUEST:
        draftState.savingMessage = true;
        break;
      case ActionTypes.SAVE_MESSAGE_INFO_SUCCESS:
        const messageIndex = draftState.items.findIndex((msi) => msi.mediaInfo.id === action.payload.message.id);
        draftState.items[messageIndex].mediaInfo = {
          ...draftState.items[messageIndex].mediaInfo,
          ...action.payload.message,
        };
        draftState.savingMessage = false;
        break;
      default:
        return draftState;
    }
  });
};
